import * as Dialog from '@radix-ui/react-dialog';
import { FiX } from 'react-icons/fi';
import { useState, useEffect } from 'react';
import BorderGradient from 'components/shared/BorderGradient/BorderGradient';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import '../../styles/tiptap.css';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
interface Guide {
  id: string;
  categoryId: string;
  name: string;
  desc: string;
  link: string;
}

interface Category {
  id: string;
  name: string;
}

interface GuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: any;
  type: string;
  onSubmit: (formData: any, type: string) => void;
  categories: { id: string; name: string; }[];
}

export default function GuideModal({ 
  isOpen, 
  onClose, 
  mode, 
  initialData, 
  type,
  onSubmit,
  categories 
}: GuideModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    videoTitle: '',
    videoDescription: ''
  });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false, // Disable default lists in StarterKit
        orderedList: false, // Disable default lists in StarterKit
      }),
      BulletList, // Enable bullet lists
    OrderedList, // Enable ordered lists
    ListItem, // Required for lists to work properly
      Placeholder.configure({
        placeholder: 'Enter video description...',
      }),
    ],
    content: formData.videoDescription,
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[200px] p-4 bg-[#2A2A2A] border border-white/[0.08] rounded-lg',
      },
    },
  });

  useEffect(() => {
    if (initialData && mode === 'edit') {
      if (type === 'guides') {
        const guide = initialData as Guide;
        setFormData({
          name: guide.name,
          category: guide.categoryId,
          videoTitle: guide.link,
          videoDescription: guide.desc
        });
        if (editor) {
          editor.commands.setContent(guide.desc);
        }
      } else {
        const category = initialData as Category;
        setFormData({
          name: category.name,
          category: '',
          videoTitle: '',
          videoDescription: ''
        });
      }
    } else {
      // Reset form data when modal is opened in create mode
      setFormData({
        name: '',
        category: '',
        videoTitle: '',
        videoDescription: ''
      });
      if (editor) {
        editor.commands.setContent('');
      }
    }
  }, [initialData, mode, type, editor, isOpen]);

  useEffect(() => {
    if (editor) {
      editor.on('update', ({ editor }) => {
        setFormData(prev => ({
          ...prev,
          videoDescription: editor.getHTML()
        }));
      });
    }
   
  }, [editor]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData, type);
    setFormData({
      name: '',
      category: '',
      videoTitle: '',
      videoDescription: ''
    });
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg z-50">
          <BorderGradient className='w-full max-w-2xl rounded-lg flex flex-col'>
            <div className="bg-[#1A1A1A] rounded-lg shadow-lg max-h-[90vh] overflow-auto">
              <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
                <Dialog.Title className="text-lg font-semibold text-white">
                  {mode === 'create' ? `Add ${type}` : `Edit ${type}`}
                </Dialog.Title>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                {type === 'guides' ? (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Guide Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-lg px-4 py-2.5 text-white"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Video Link
                      </label>
                      <input
                        type="text"
                        value={formData.videoTitle}
                        onChange={(e) => setFormData({ ...formData, videoTitle: e.target.value })}
                        className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-lg px-4 py-2.5 text-white"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Video Description
                      </label>
                      <div className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-lg overflow-hidden">
                        <div className="border-b border-white/[0.08] bg-[#1A1A1A] p-2 flex gap-1">
                          <button
                            type="button"
                            onClick={() => editor?.chain().focus().toggleBold().run()}
                            className={`p-2 rounded ${editor?.isActive('bold') ? 'bg-[#4799e9] text-white' : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a33]'}`}
                          >
                            Bold
                          </button>
                          <button
                            type="button"
                            onClick={() => editor?.chain().focus().toggleItalic().run()}
                            className={`p-2 rounded ${editor?.isActive('italic') ? 'bg-[#4799e9] text-white' : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a33]'}`}
                          >
                            Italic
                          </button>
                          <button
                            type="button"
                            onClick={() => editor?.chain().focus().toggleBulletList().run()}
                            className={`p-2 rounded ${editor?.isActive('bulletList') ? 'bg-[#4799e9] text-white' : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a33]'}`}
                          >
                            Bullet List
                          </button>
                          <button
                            type="button"
                            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                            className={`p-2 rounded ${editor?.isActive('orderedList') ? 'bg-[#4799e9] text-white' : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a33]'}`}
                          >
                            Numbered List
                          </button>
                        </div>
                        <EditorContent editor={editor} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-lg px-4 py-2.5 text-white"
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Category Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-lg px-4 py-2.5 text-white"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#2A2A2A] text-white rounded-lg hover:bg-[#333333] transition-colors"
                  >
                    {mode === 'create' ? 'Create' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </BorderGradient>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
} 